import React, { useEffect } from "react";
import Header from "../components/Header";
import { motion } from "framer-motion";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { gerarLinkWhatsApp } from "../../utils/WhatsappLink";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CheckIcon from "@mui/icons-material/Check";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import SentimentVerySatisfiedOutlinedIcon from "@mui/icons-material/SentimentVerySatisfiedOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";

// Padrão visual compartilhado com as seções da home
const eyebrowSx = (dark) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 1.75,
    py: 0.75,
    borderRadius: "999px",
    backgroundColor: dark ? "rgba(255, 200, 69, 0.12)" : "rgba(255, 200, 69, 0.25)",
    border: dark ? "1px solid rgba(255, 200, 69, 0.4)" : "none",
    color: dark ? "#ffc845" : "#0b243d",
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
});

const primaryButtonSx = {
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    backgroundColor: "#ffc845",
    color: "#0b243d",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(255, 200, 69, 0.35)",
    transition: "all 0.2s ease",
    '&:hover': {
        backgroundColor: "#e9b53d",
        transform: "translateY(-2px)",
        boxShadow: "0 10px 28px rgba(255, 200, 69, 0.45)",
    },
};

const outlinedDarkButtonSx = {
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    color: "#fff",
    borderColor: "rgba(255, 255, 255, 0.4)",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    '&:hover': {
        borderColor: "#fff",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        transform: "translateY(-2px)",
    },
};

// Card de um nicho
const NicheCard = ({ title, description, icon: Icon, index, onClick }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: (index % 4) * 0.08 }}
        viewport={{ once: true, amount: 0.2 }}
        style={{ height: "100%" }}
    >
        <Box
            onClick={onClick}
            sx={{
                height: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                p: { xs: 2.5, md: 3 },
                backgroundColor: "#fff",
                border: "1px solid #ece4d3",
                borderRadius: "18px",
                cursor: "pointer",
                transition: "all 0.25s ease",
                "& .niche-icon": { transition: "all 0.25s ease" },
                "& .niche-cta": { transition: "gap 0.25s ease" },
                "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "#ffc845",
                    boxShadow: "0 18px 40px rgba(11, 36, 61, 0.12)",
                },
                "&:hover .niche-icon": { backgroundColor: "#0b243d", color: "#ffc845" },
                "&:hover .niche-cta": { gap: 1.25 },
            }}
        >
            <Box
                className="niche-icon"
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    backgroundColor: "rgba(255, 200, 69, 0.22)",
                    color: "#0b243d",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Icon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ mt: 2, fontSize: "1.15rem", fontWeight: 700, color: "#0b243d" }}>{title}</Box>
            <Box sx={{ mt: 1, flex: 1, fontSize: "0.92rem", lineHeight: 1.6, color: "#4b5563" }}>{description}</Box>
            <Box
                className="niche-cta"
                sx={{ mt: 2.5, display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.9rem", fontWeight: 700, color: "#c8961e" }}
            >
                Ver planos
                <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Box>
        </Box>
    </motion.div>
);

// Card de destaque para quem não encontrou a categoria
const ContactCard = ({ title, description, link, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: (index % 4) * 0.08 }}
        viewport={{ once: true, amount: 0.2 }}
        style={{ height: "100%" }}
    >
        <Box
            onClick={() => window.open(link, "_blank")}
            sx={{
                position: "relative",
                height: "100%",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                p: { xs: 2.5, md: 3 },
                background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                color: "#fff",
                borderRadius: "18px",
                cursor: "pointer",
                overflow: "hidden",
                transition: "all 0.25s ease",
                "&:hover": { transform: "translateY(-4px)", boxShadow: "0 18px 40px rgba(11, 36, 61, 0.3)" },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: -50,
                    right: -50,
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    border: "26px solid #ffc845",
                    opacity: 0.18,
                }}
            />
            <Box
                sx={{
                    position: "relative",
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    backgroundColor: "#ffc845",
                    color: "#0b243d",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <SupportAgentOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ position: "relative", mt: 2, fontSize: "1.15rem", fontWeight: 700 }}>{title}</Box>
            <Box sx={{ position: "relative", mt: 1, flex: 1, fontSize: "0.92rem", lineHeight: 1.6, color: "rgba(255, 255, 255, 0.8)" }}>{description}</Box>
            <Box
                sx={{
                    position: "relative",
                    mt: 2.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    width: "fit-content",
                    px: 2,
                    py: 1,
                    borderRadius: "10px",
                    backgroundColor: "#25D366",
                    fontSize: "0.9rem",
                    fontWeight: 700,
                }}
            >
                <WhatsAppIcon sx={{ fontSize: 18 }} />
                Falar conosco
            </Box>
        </Box>
    </motion.div>
);

const Niches = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const listNiches = [
        {
            title: "Médicos",
            description: "Contabilidade especializada para médicos, com foco em economia tributária e segurança fiscal.",
            icon: MedicalServicesOutlinedIcon,
        },
        {
            title: "Dentistas",
            description: "Soluções contábeis específicas para clínicas e consultórios odontológicos.",
            icon: SentimentVerySatisfiedOutlinedIcon,
        },
        {
            title: "Psicólogos",
            description: "Organização fiscal e contábil sob medida para psicólogos autônomos ou clínicas.",
            icon: PsychologyOutlinedIcon,
        },
        {
            title: "Fisioterapeutas",
            description: "Gestão contábil eficiente para quem atua com reabilitação e terapias físicas.",
            icon: AccessibilityNewOutlinedIcon,
        },
        {
            title: "Nutricionistas",
            description: "Serviços contábeis adaptados à rotina e legislação da nutrição.",
            icon: RestaurantOutlinedIcon,
        },
        {
            title: "Fonoaudiólogos",
            description: "Cuidamos da parte fiscal para que você cuide da comunicação das pessoas.",
            icon: RecordVoiceOverOutlinedIcon,
        },
        {
            title: "Biomédicos",
            description: "Atendimento contábil específico para quem atua em análises clínicas e laboratoriais.",
            icon: BiotechOutlinedIcon,
        },
        {
            title: "Personal Trainers",
            description: "Contabilidade para profissionais da educação física que atuam com treinamento personalizado, com foco na legalização e gestão eficiente dos ganhos.",
            icon: FitnessCenterOutlinedIcon,
        },
        {
            title: "Não encontrou sua categoria?",
            description: "Clique e fale conosco para saber como podemos ajudar sua profissão.",
            link: gerarLinkWhatsApp("Olá! Não encontrei minha categoria na lista. Poderiam verificar se atendem minha profissão e me passar mais detalhes?"),
        },
    ];

    return (
        <>
            <Header active="nichos" />

            {/* Seção de introdução */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    pt: { xs: "120px", md: "140px" },
                    pb: { xs: 8, md: 10 },
                    overflow: "hidden",
                }}
            >
                {/* Detalhe decorativo */}
                <Box
                    sx={{
                        position: "absolute",
                        bottom: { xs: -100, md: -160 },
                        right: { xs: -100, md: -160 },
                        width: { xs: 240, md: 380 },
                        height: { xs: 240, md: 380 },
                        borderRadius: "50%",
                        border: { xs: "36px solid #ffc845", md: "56px solid #ffc845" },
                        opacity: 0.1,
                        pointerEvents: "none",
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        width: "90%",
                        maxWidth: "1280px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        alignItems: "center",
                        gap: { xs: 6, md: 8 },
                    }}
                >
                    {/* Imagem (no mobile fica abaixo do texto) */}
                    <Box sx={{ order: { xs: 2, md: 1 } }}>
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <Box sx={{ position: "relative", maxWidth: { xs: "520px", md: "none" }, mx: "auto" }}>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: { xs: -24, md: -36 },
                                        left: { xs: -24, md: -36 },
                                        width: { xs: 110, md: 160 },
                                        height: { xs: 110, md: 160 },
                                        borderRadius: "50%",
                                        border: { xs: "20px solid #ffc845", md: "28px solid #ffc845" },
                                        opacity: 0.35,
                                    }}
                                />
                                <Box
                                    component="img"
                                    src="/assets/nichos.jpeg"
                                    alt="Profissionais da saúde"
                                    sx={{
                                        position: "relative",
                                        display: "block",
                                        width: "100%",
                                        height: "auto",
                                        borderRadius: "24px",
                                        objectFit: "cover",
                                        boxShadow: "0 24px 60px rgba(0, 0, 0, 0.4)",
                                    }}
                                />
                            </Box>
                        </motion.div>
                    </Box>

                    {/* Texto */}
                    <Box sx={{ order: { xs: 1, md: 2 } }}>
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <Box sx={{ ...eyebrowSx(true), mb: 2.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                                Nichos
                            </Box>
                            <h1 style={{ margin: "0 0 20px", fontSize: "clamp(2rem, 4.4vw, 3.2rem)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.5px" }}>
                                Cada especialidade tem sua rotina.{" "}
                                <span style={{ color: "#ffc845" }}>Sua contabilidade também.</span>
                            </h1>
                            <Box component="p" sx={{ m: 0, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.82)" }}>
                                Entendemos que cada profissão tem suas particularidades. Por isso, oferecemos um atendimento contábil personalizado para médicos, dentistas, psicólogos, fisioterapeutas, nutricionistas, fonoaudiólogos, biomédicos e demais profissionais da área da saúde, com foco em <b style={{ color: "#fff" }}>economia tributária, organização financeira e segurança fiscal.</b>
                            </Box>

                            <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                                <Button
                                    variant="contained"
                                    onClick={() => document.getElementById("lista-nichos")?.scrollIntoView({ behavior: "smooth" })}
                                    sx={primaryButtonSx}
                                >
                                    Encontrar minha profissão
                                </Button>
                                <Button variant="outlined" endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Planos")} sx={outlinedDarkButtonSx}>
                                    Ver planos
                                </Button>
                            </Box>
                        </motion.div>
                    </Box>
                </Box>
            </Box>

            {/* Cards de nichos */}
            <Box
                id="lista-nichos"
                component="section"
                sx={{ width: "100%", display: "flex", justifyContent: "center", backgroundColor: "#f9f2e4", py: { xs: 8, md: 12 }, scrollMarginTop: "80px" }}
            >
                <Box sx={{ width: "90%", maxWidth: "1280px" }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
                            <Box sx={{ ...eyebrowSx(false), mb: 2.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
                                Quem atendemos
                            </Box>
                            <h2 style={{ margin: "0 0 12px", fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", color: "#0b243d" }}>
                                Encontre a <span style={{ color: "#c8961e" }}>sua profissão</span>
                            </h2>
                            <Box component="p" sx={{ m: "0 auto", maxWidth: "560px", fontSize: { xs: "1rem", md: "1.05rem" }, lineHeight: 1.7, color: "#4b5563" }}>
                                Contabilidade sob medida para as principais áreas da saúde.
                            </Box>
                        </Box>
                    </motion.div>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                            gap: { xs: 2, md: 2.5 },
                        }}
                    >
                        {listNiches.map((niche, index) =>
                            niche.link ? (
                                <ContactCard key={index} index={index} title={niche.title} description={niche.description} link={niche.link} />
                            ) : (
                                <NicheCard
                                    key={index}
                                    index={index}
                                    title={niche.title}
                                    description={niche.description}
                                    icon={niche.icon}
                                    onClick={() => navigate("../Planos")}
                                />
                            )
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Chamada final */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    py: { xs: 8, md: 10 },
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: -90, md: -120 },
                        left: { xs: -90, md: -120 },
                        width: { xs: 220, md: 320 },
                        height: { xs: 220, md: 320 },
                        borderRadius: "50%",
                        border: { xs: "36px solid #ffc845", md: "52px solid #ffc845" },
                        opacity: 0.12,
                        pointerEvents: "none",
                    }}
                />
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    style={{ position: "relative", zIndex: 1, width: "90%", maxWidth: "760px", textAlign: "center" }}
                >
                    <h2 style={{ margin: "0 0 16px", fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px" }}>
                        Encontrou sua especialidade? <span style={{ color: "#ffc845" }}>Vamos começar.</span>
                    </h2>
                    <Box component="p" sx={{ m: "0 auto", maxWidth: "580px", fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.8)" }}>
                        Escolha o plano ideal para a sua rotina ou fale com um consultor para tirar suas dúvidas.
                    </Box>
                    <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "center", gap: 1.5 }}>
                        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Planos")} sx={primaryButtonSx}>
                            Ver planos
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => window.open(gerarLinkWhatsApp("Olá! Sou profissional da saúde e gostaria de saber mais sobre a contabilidade da Clarea."), "_blank")}
                            sx={outlinedDarkButtonSx}
                        >
                            Falar com um consultor
                        </Button>
                    </Box>
                    <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, fontSize: "0.9rem", color: "rgba(255, 255, 255, 0.75)" }}>
                        <CheckIcon sx={{ fontSize: 18, color: "#ffc845" }} />
                        Sem multa e sem fidelidade
                    </Box>
                </motion.div>
            </Box>

            <Footer />
        </>
    );
};

export default Niches;
